import React, { startTransition, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as S from './SearchPanelForm.styles';
import { SearchPanelOverlay } from './SearchPanelOverlay.styles';
import { KeywordsSource } from './SearchSuggestions.types';
import { MIN_QUERY_LENGTH } from './SearchSuggestions.constants';
import { SearchSuggestions } from './SearchSuggestions';
import { shouldSkipSearchCategoryQuery } from './SearchPanelForm.helpers';
import { useSearchStore } from '@/store/useSearchStore';
import { useResponsive } from '@/hooks/useResponsive';
import { useSearch } from '@/hooks/useSearch';
import { useQueryParam } from '@/hooks/useQueryParam';
import { QUERY } from '@/constants/common';
import {
  GetSearchSuggestionResponse,
  Properties,
  SearchSuggestion,
} from '@/api/types/searchSuggestions.types';
import { useNavMenuStore } from '@/store/useNavMenuStore';
import { useDesktopCatalogueStore } from '@/store/useDesktopCatalogueStore';
import { GET_SEARCH_SUGGESTIONS } from '@/api/queries/searchSuggestions.queries';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import NoSsr from '@/components/NoSsr/NoSsr';
import { Portal } from '@/components/UI/Portal/Portal';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useOptionalServerDataContext } from '@/contexts';

export const SearchPanelForm = () => {
  const search = useSearch();
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const { currentCategory: initialCategory } =
    useOptionalServerDataContext() ?? {};
  const { query } = router;
  const shouldSkipCategoryQuery = shouldSkipSearchCategoryQuery(query);
  const { currentCategory } = useCurrentCategory({
    skip: shouldSkipCategoryQuery,
  });
  const [searchQueryFromUrl] = useQueryParam(`${QUERY.q}[${QUERY.keywords}]`);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isNavOpen, toggleNav] = useNavMenuStore(state => [
    state.isNavOpen,
    state.toggleNav,
  ]);
  const [isDesktopCatalogueOpen, toggleDesktopCatalogue] =
    useDesktopCatalogueStore(state => [
      state.isDesktopCatalogueOpen,
      state.toggleDesktopCatalogue,
    ]);
  const [isSuggestionsOpened, setIsSuggestionsOpened] = useSearchStore(
    state => [state.isSuggestionsOpened, state.setIsSuggestionsOpened]
  );
  const setTopControlsCanBeFixed = usePageTopControlsStore(
    state => state.setCanBeFixed
  );

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>(
    searchQueryFromUrl || ''
  );
  const [isSuggestionsQuerySkipped, setIsSuggestionsQuerySkipped] =
    useState(true);

  useQuery<GetSearchSuggestionResponse>(GET_SEARCH_SUGGESTIONS, {
    skip: !searchQuery.trim() || isSuggestionsQuerySkipped,
    fetchPolicy: 'no-cache',
    variables: { keywords: searchQuery.trim() },
    onCompleted: data => {
      const formattedSuggestions = data.searchSuggestions.map(item => {
        const propertiesJSON = item.properties?.replace?.(/=>/g, ':') ?? '{}';

        return {
          ...item,
          properties: JSON.parse(propertiesJSON) as Properties,
        };
      });

      setSuggestions(formattedSuggestions);
      setIsSuggestionsOpened(!!formattedSuggestions.length);
      setIsSuggestionsQuerySkipped(true);
    },
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const isFormFocused = isFocused || isSuggestionsOpened;
  const { t } = useTranslation();
  const isClearButtonShown = !!searchQuery && isFocused;
  const category = currentCategory ?? initialCategory;
  const isCategorySearch = Boolean(category?.name && !shouldSkipCategoryQuery);
  const searchPlaceholder = isCategorySearch
    ? t('search.category_placeholder', { name: category?.name })
    : t('search.placeholder');

  function closeDropdowns() {
    setIsFocused(false);
    setIsSuggestionsOpened(false);
  }

  function renderOverlay() {
    const Wrapper = isDesktop ? NoSsr : Portal;

    return (
      <Wrapper>
        <SearchPanelOverlay
          data-testid={CommonTestIds.searchPanel.overlay}
          isOpen={isSuggestionsOpened}
          onClick={closeDropdowns}
        />
      </Wrapper>
    );
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    closeDropdowns();
    search(searchQuery.trim(), { keywordsSource: KeywordsSource.Typewritten });
    inputRef?.current?.blur();
  }

  function onSearch(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchQuery(e.target.value);
    startTransition(() => {
      if (e.target.value.length < MIN_QUERY_LENGTH) {
        setIsSuggestionsOpened(false);
      } else {
        setIsSuggestionsQuerySkipped(false);
      }
    });
  }

  function toggleDropdowns() {
    if (isNavOpen) toggleNav();
    if (isDesktopCatalogueOpen) toggleDesktopCatalogue();
  }

  function onInputFocus() {
    setIsFocused(true);

    if (suggestions.length) {
      setIsSuggestionsOpened(!!searchQuery.trim());
    } else {
      setIsSuggestionsQuerySkipped(false);
    }

    toggleDropdowns();
  }

  function onInputClear(e: React.MouseEvent) {
    e.preventDefault();
    setIsFocused(true);
    setSearchQuery('');
    setIsSuggestionsOpened(false);
  }

  function scrollToInput() {
    inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function onSuggestionSelect(suggestion: SearchSuggestion) {
    setSearchQuery(suggestion.value);
    setIsSuggestionsQuerySkipped(true);
    setIsSuggestionsOpened(false);
    scrollToInput();
    localStorage.setItem(STORAGE_KEYS.NEED_TO_RESTORE_SCROLL_POSITION, 'false');

    search(suggestion.value, {
      keywordsSource: KeywordsSource.Suggestion,
      ...(suggestion.properties && {
        properties: suggestion.properties,
        categoryPath: suggestion.categoryPath,
      }),
    });
  }

  useEffect(() => {
    if (!isDesktop) {
      setTopControlsCanBeFixed(!isSuggestionsOpened);

      if (!isSuggestionsOpened && !searchQuery.trim()) {
        setSuggestions([]);
      }
    }
  }, [isDesktop, isSuggestionsOpened, searchQuery]);

  useEffect(() => {
    setSearchQuery(searchQueryFromUrl || '');
  }, [router.asPath, searchQueryFromUrl]);

  const offsetLeft = wrapperRef.current?.getBoundingClientRect()?.left;

  return (
    <S.SearchPanelFormWrapper ref={wrapperRef}>
      {renderOverlay()}

      <S.SearchPanelFormBox
        onSubmit={onSubmit}
        data-testid="search-panel-form-box"
      >
        <S.SearchPanelFormContent $isFocused={isFormFocused}>
          <S.SearchPanelBtn
            {...(isDesktop
              ? { onMouseDown: closeDropdowns }
              : { onClick: onSubmit, className: 'top-search__form-btn' })}
            data-testid="search-panel-btn"
          >
            <S.SearchPanelBtnIcon />
          </S.SearchPanelBtn>

          <S.SearchPanelInput
            ref={inputRef}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={onSearch}
            onFocus={onInputFocus}
            onBlur={() => setIsFocused(false)}
            data-testid={CommonTestIds.searchPanel.input}
          />

          {isClearButtonShown && (
            <S.SearchPanelClearBtn
              onClick={onInputClear}
              onMouseDown={e => e.preventDefault()}
              data-testid="search-panel-clear-btn"
            >
              <S.SearchPanelClearBtnIcon />
            </S.SearchPanelClearBtn>
          )}
        </S.SearchPanelFormContent>

        {isDesktop && (
          <S.DesktopSearchPanelBtn
            className="header-btn js-header-btn header-btn_search"
            $isFormFocused={isFormFocused}
            onClick={onSubmit}
            data-testid="desktop-search-panel-btn"
          >
            {t('search.button')}
          </S.DesktopSearchPanelBtn>
        )}
      </S.SearchPanelFormBox>

      <NoSsr>
        <SearchSuggestions
          searchQuery={searchQuery.trim()}
          suggestions={suggestions}
          onSelect={onSuggestionSelect}
          offsetLeft={offsetLeft}
        />
      </NoSsr>
    </S.SearchPanelFormWrapper>
  );
};
